import { ref, computed } from 'vue';
import { useSubscription } from '@/features/subscription/useSubscription.js';
import { authApi } from '@/api/index.js';

const email = ref<string | null>(null);
const role = ref<'owner' | 'manager' | 'employee' | null>(null);
const isAuthenticated = computed(() => email.value !== null);
const isManagerOrOwner = computed(() => role.value === 'owner' || role.value === 'manager');
const isOwner = computed(() => role.value === 'owner');

function restoreSessionFromToken() {
  const token = localStorage.getItem('punchlog_session_token');
  if (!token) return;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return;
    const payload = JSON.parse(atob(parts[1].replaceAll('-', '+').replaceAll('_', '/')));
    if (payload.email) email.value = payload.email;
    if (payload.role) role.value = payload.role;
  } catch {
    // Malformed token — clear it
    localStorage.removeItem('punchlog_session_token');
  }
}

// Restore on module load
restoreSessionFromToken();

export function useAuth() {
  const { syncTier, tenantId } = useSubscription();

  async function requestCode(addr: string) {
    try {
      await authApi.requestCode(addr, tenantId());
    } catch (e: any) {
      throw new Error(e.message ?? 'Failed to send code');
    }
  }

  async function verifyCode(addr: string, code: string) {
    let data: { session_token: string; tenant_id: string };
    try {
      data = await authApi.verifyCode(addr, code, tenantId());
    } catch (e: any) {
      throw new Error(e.message ?? 'Invalid code');
    }

    // Store session token
    localStorage.setItem('punchlog_session_token', data.session_token);

    // Overwrite the device's tenant_id with the authoritative one from the response
    localStorage.setItem('punchlog_tenant_id', data.tenant_id);

    email.value = addr;

    // Decode role from the freshly-issued JWT (same pattern as restoreSessionFromToken)
    try {
      const parts = data.session_token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replaceAll('-', '+').replaceAll('_', '/')));
        if (payload.role) role.value = payload.role;
      }
    } catch {
      /* malformed token — leave role as-is */
    }

    // useSubscription()'s tier/status/etc. are module-level singletons already populated for
    // this device's PREVIOUS (possibly anonymous) tenant_id — without an explicit refresh here,
    // PlanStatusStrip/PlanManageCard would keep showing stale state until an unrelated reload.
    // This matters most for the cross-device case, where tenant_id just changed identity.
    await syncTier({ force: true });
  }

  async function logout() {
    // Best-effort call to server — actual logout is discarding the local token regardless
    await authApi.logout().catch(() => {});
    localStorage.removeItem('punchlog_session_token');
    // Do NOT touch punchlog_tenant_id — signing out returns to local/anonymous use
    email.value = null;
    role.value = null;
  }

  return {
    email,
    isAuthenticated,
    isManagerOrOwner,
    isOwner,
    logout,
    requestCode,
    role,
    verifyCode,
  };
}
